import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db } from '../lib/firebase';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BRAND = '#214140';
const GREEN = '#1F8051';
const LIGHT = '#DFEAF0';
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-semibold mb-1" style={{ color: BRAND }}>
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2";

export default function DistributorApplyPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', confirm_password: '',
    company: '', territory: '', role_title: '', phone: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await sendEmailVerification(user, { url: window.location.origin + '/distributors/login' });
      await setDoc(doc(db, 'user_profiles', user.uid), {
        full_name: form.full_name, email: form.email, company: form.company,
        territory: form.territory, role_title: form.role_title, phone: form.phone,
        message: form.message, access_level: 'pending', created_at: serverTimestamp(),
      });
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: 'New Distributor Application from ' + form.full_name,
          from_name: 'Cordflex Distributor Portal', to: 'info@nursetechmedical.com',
          name: form.full_name, email: form.email, company: form.company,
          territory: form.territory || 'Not specified', role: form.role_title || 'Not specified',
          phone: form.phone || 'Not specified', message: form.message || 'No message provided',
          admin_link: window.location.origin + '/distributors/admin',
        })
      });
      await auth.signOut();
      setSuccess(true);
    } catch (err) {
      setError(err.code === 'auth/email-already-in-use'
        ? 'An account with this email already exists. Try signing in.'
        : (err.message || 'Something went wrong. Please try again.'));
    } finally { setLoading(false); }
  };

  if (success) return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="max-w-md w-full text-center">
          <div className="text-5xl mb-6">✉️</div>
          <h2 className="text-2xl font-bold mb-3" style={{ color: BRAND }}>Check Your Email</h2>
          <p className="text-gray-600 mb-4">
            We sent a verification link to <strong>{form.email}</strong>.
            Click the link in that email to confirm your account.
          </p>
          <p className="text-sm text-gray-400 mb-8">
            After verifying, sign in here. We will review your application and upgrade your access shortly.
          </p>
          <button onClick={() => navigate('/distributors/login')}
            className="px-8 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90"
            style={{ background: BRAND }}>
            Go to Sign In
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="flex-1 px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ color: BRAND }}>Apply to Become a Distributor</h1>
            <p className="text-gray-500">Tell us about yourself and your distribution network. We will review your application and be in touch.</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-8" style={{ border: '1px solid ' + LIGHT }}>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="pb-4" style={{ borderBottom: '1px solid ' + LIGHT }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: GREEN }}>Account Credentials</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Full Name" required><input className={inputCls} value={form.full_name} onChange={set('full_name')} required /></Field>
                  <Field label="Work Email" required><input type="email" className={inputCls} value={form.email} onChange={set('email')} required /></Field>
                  <Field label="Password" required><input type="password" className={inputCls} placeholder="Min. 8 characters" value={form.password} onChange={set('password')} required minLength={8} /></Field>
                  <Field label="Confirm Password" required><input type="password" className={inputCls} value={form.confirm_password} onChange={set('confirm_password')} required /></Field>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: GREEN }}>Company and Territory</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Company / Distribution Company" required><input className={inputCls} value={form.company} onChange={set('company')} required /></Field>
                  <Field label="Territory / Region"><input className={inputCls} placeholder="e.g. Southeast US, Texas" value={form.territory} onChange={set('territory')} /></Field>
                  <Field label="Your Role / Title"><input className={inputCls} placeholder="e.g. VP Sales, Territory Manager" value={form.role_title} onChange={set('role_title')} /></Field>
                  <Field label="Phone Number"><input type="tel" className={inputCls} value={form.phone} onChange={set('phone')} /></Field>
                </div>
              </div>
              <Field label="Why are you interested in distributing Cordflex?">
                <textarea rows={4} className={inputCls} placeholder="Tell us about your network, current product lines, and why this is a fit..." value={form.message} onChange={set('message')} />
              </Field>
              {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND }}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
              <p className="text-center text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/distributors/login" className="font-semibold hover:underline" style={{ color: BRAND }}>Sign in</Link>
              </p>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
